import React, { useState, useRef, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ScatterChart, Scatter,
  Cell, ReferenceLine,
} from 'recharts';
import {
  PROVINCES, CLUSTERS, PHYLO_TREE, SNP_MATRIX,
  HOTSPOTS, SOCIODEM, TREND_DATA,
} from './data/geoData';

// ── DESIGN TOKENS — Surveillance Command Centre ───────────────────────
const T = {
  bg:         '#080C14',
  bgCard:     '#0E1520',
  bgElevated: '#141E2E',
  bgPanel:    '#1A2640',
  border:     '#1E3050',
  borderGlow: '#1E5F8A',
  navy:       '#0A1628',
  blue:       '#1B6CA8',
  blueLight:  '#2980B9',
  cyan:       '#00D4FF',
  cyanDim:    '#00D4FF30',
  teal:       '#00897B',
  green:      '#00C853',
  greenDim:   '#00C85330',
  amber:      '#FFB300',
  amberDim:   '#FFB30030',
  red:        '#FF1744',
  redDim:     '#FF174430',
  purple:     '#AA00FF',
  purpleDim:  '#AA00FF30',
  text:       '#E0ECF8',
  textMuted:  '#7A99B8',
  textDim:    '#3D5A78',
  white:      '#FFFFFF',
  grid:       '#0D1826',
};

const ALERT_CFG = {
  critical: { color: T.red,    bg: T.redDim,    label: 'CRITICAL', glow: `0 0 20px ${T.red}60`   },
  high:     { color: T.amber,  bg: T.amberDim,  label: 'HIGH',     glow: `0 0 12px ${T.amber}50` },
  moderate: { color: T.blue,   bg: T.cyanDim,   label: 'MODERATE', glow: 'none'                   },
  low:      { color: T.green,  bg: T.greenDim,  label: 'LOW',      glow: 'none'                   },
};

const CLS_COLOR = {
  'Pre-XDR-TB': T.purple, 'XDR-TB': '#FF0040', 'MDR-TB': T.red,
  'RR-TB': T.amber, 'DS-TB': T.green,
};

const SANS = "'Inter', system-ui, sans-serif";
const MONO = "'JetBrains Mono', monospace";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; min-height: 100vh; }
  body { background: ${T.bg}; color: ${T.text}; font-family: ${SANS}; font-size: 14px; overflow-x: hidden; }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: ${T.bg}; }
  ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(400%)} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes ripple { 0%{transform:scale(1);opacity:0.8} 100%{transform:scale(3);opacity:0} }
  .fade-up { animation: fadeUp 0.25s ease forwards; }
  .pulse   { animation: pulse 2s infinite; }
`;

// ── HELPERS ───────────────────────────────────────────────────────────
const card = (children, style = {}) => (
  <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, ...style }}>{children}</div>
);

const badge = (label, color, pulse) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, color, background: color + '20', border: `1px solid ${color}40`, animation: pulse ? 'pulse 1.5s infinite' : 'none' }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block' }} />
    {label}
  </span>
);

// ── MAP COMPONENT (SVG-based Zimbabwe map) ────────────────────────────
function ZimbabweMap({ provinces, clusters, selectedProvince, onSelectProvince, overlay }) {
  // SVG coordinate system — Zimbabwe fits in ~600x400 viewbox
  // Longitude 25.2–33.1 → x: 0–600
  // Latitude -15.6 to -22.4 → y: 0–400
  const lngToX = lng => ((lng - 25.2) / (33.1 - 25.2)) * 580 + 10;
  const latToY = lat => ((lat - (-15.6)) / ((-22.4) - (-15.6))) * 380 + 10;

  const getSizeByTB = (cases) => {
    if (cases > 4000) return 28;
    if (cases > 2000) return 22;
    if (cases > 1000) return 16;
    if (cases > 700)  return 12;
    return 9;
  };

  const getColor = (p) => {
    if (overlay === 'mdr') {
      const mdrPct = (p.mdr_cases / p.tb_cases * 100);
      if (mdrPct > 10) return T.red;
      if (mdrPct > 6)  return T.amber;
      if (mdrPct > 3)  return T.blue;
      return T.green;
    }
    if (overlay === 'rate') {
      if (p.tb_rate > 300) return T.red;
      if (p.tb_rate > 150) return T.amber;
      if (p.tb_rate > 100) return T.blueLight;
      return T.green;
    }
    return p.color || T.blue;
  };

  return (
    <svg viewBox="0 0 600 410" style={{ width: '100%', height: '100%' }}>
      {/* Background */}
      <rect width="600" height="410" fill={T.bgElevated} rx="8" />

      {/* Grid lines */}
      {[26,27,28,29,30,31,32,33].map(lng => (
        <line key={lng} x1={lngToX(lng)} y1={10} x2={lngToX(lng)} y2={390} stroke={T.border} strokeWidth={0.5} strokeDasharray="4,4" />
      ))}
      {[-16,-17,-18,-19,-20,-21,-22].map(lat => (
        <line key={lat} x1={10} y1={latToY(lat)} x2={590} y2={latToY(lat)} stroke={T.border} strokeWidth={0.5} strokeDasharray="4,4" />
      ))}

      {/* Province circles */}
      {provinces.map(p => {
        const x = lngToX(p.lng);
        const y = latToY(p.lat);
        const r = getSizeByTB(p.tb_cases);
        const col = getColor(p);
        const isSelected = selectedProvince?.id === p.id;
        const alert = ALERT_CFG[p.alert];

        return (
          <g key={p.id} onClick={() => onSelectProvince(p)} style={{ cursor: 'pointer' }}>
            {/* Ripple for critical/high */}
            {(p.alert === 'critical' || p.alert === 'high') && (
              <circle cx={x} cy={y} r={r * 2.5} fill="none" stroke={col} strokeWidth={1} opacity={0.3}
                style={{ animation: 'ripple 2s infinite' }} />
            )}
            {/* Glow ring */}
            {isSelected && <circle cx={x} cy={y} r={r + 8} fill="none" stroke={T.cyan} strokeWidth={2} />}
            {/* Main circle */}
            <circle cx={x} cy={y} r={r} fill={col + '40'} stroke={col} strokeWidth={isSelected ? 2.5 : 1.5}
              style={{ filter: p.alert === 'critical' ? `drop-shadow(0 0 8px ${col})` : 'none' }} />
            {/* Label */}
            <text x={x} y={y + r + 12} textAnchor="middle" fontSize={9} fill={T.textMuted} fontFamily={SANS}>{p.name}</text>
            <text x={x} y={y + 3} textAnchor="middle" fontSize={9} fill={T.white} fontFamily={MONO} fontWeight="700">{p.tb_cases.toLocaleString()}</text>
          </g>
        );
      })}

      {/* Cluster markers */}
      {clusters.map(c => {
        const x = lngToX(c.location.lng);
        const y = latToY(c.location.lat);
        const col = c.alert_level === 'critical' ? T.red : c.alert_level === 'high' ? T.amber : T.blue;
        return (
          <g key={c.id}>
            <polygon points={`${x},${y-10} ${x+8},${y+6} ${x-8},${y+6}`} fill={col + '40'} stroke={col} strokeWidth={1.5}
              style={{ filter: `drop-shadow(0 0 6px ${col})` }} />
            <text x={x} y={y + 18} textAnchor="middle" fontSize={8} fill={col} fontFamily={MONO} fontWeight="600">
              {c.size}🔺
            </text>
          </g>
        );
      })}

      {/* Compass */}
      <g transform="translate(555, 25)">
        <circle cx={0} cy={0} r={14} fill={T.bgPanel} stroke={T.border} strokeWidth={1} />
        <text x={0} y={-5} textAnchor="middle" fontSize={8} fill={T.cyan} fontWeight="700" fontFamily={MONO}>N</text>
        <line x1={0} y1={-10} x2={0} y2={10} stroke={T.border} strokeWidth={0.5} />
        <line x1={-10} y1={0}  x2={10} y2={0} stroke={T.border} strokeWidth={0.5} />
      </g>

      {/* Scale */}
      <g transform="translate(10, 390)">
        <line x1={0} y1={0} x2={60} y2={0} stroke={T.textDim} strokeWidth={1} />
        <line x1={0} y1={-4} x2={0} y2={4} stroke={T.textDim} strokeWidth={1} />
        <line x1={60} y1={-4} x2={60} y2={4} stroke={T.textDim} strokeWidth={1} />
        <text x={30} y={-6} textAnchor="middle" fontSize={8} fill={T.textDim} fontFamily={SANS}>~100km</text>
      </g>
    </svg>
  );
}

// ── PHYLOGENETIC TREE (SVG) ───────────────────────────────────────────
function PhyloTree({ selectedCluster }) {
  const treeData = [
    // Lineage 4 branch
    { id: 'root',    x: 20,  y: 200, px: null, py: null,     label: 'Root', type: 'internal', lineage: null,  cluster: null        },
    { id: 'L4',      x: 80,  y: 120, px: 20,  py: 200,       label: 'L4',   type: 'internal', lineage: 'L4',  cluster: null        },
    { id: 'L2',      x: 80,  y: 300, px: 20,  py: 200,       label: 'L2',   type: 'internal', lineage: 'L2',  cluster: null        },
    { id: 'clu1',    x: 160, y: 70,  px: 80,  py: 120,       label: 'CLU-001', type: 'internal', cluster: 'CLU-001' },
    { id: 'clu3',    x: 160, y: 180, px: 80,  py: 120,       label: 'CLU-003', type: 'internal', cluster: 'CLU-003' },
    { id: 'clu2',    x: 160, y: 300, px: 80,  py: 300,       label: 'CLU-002', type: 'internal', cluster: 'CLU-002' },
    // CLU-001 leaves
    { id: 'HAR001',  x: 280, y: 30,  px: 160, py: 70,        label: 'HAR001 ★', type: 'leaf', cluster: 'CLU-001', cls: 'Pre-XDR-TB', index: true  },
    { id: 'HAR002',  x: 280, y: 60,  px: 160, py: 70,        label: 'HAR002',   type: 'leaf', cluster: 'CLU-001', cls: 'Pre-XDR-TB', index: false },
    { id: 'HAR003',  x: 280, y: 90,  px: 160, py: 70,        label: 'HAR003',   type: 'leaf', cluster: 'CLU-001', cls: 'Pre-XDR-TB', index: false },
    { id: 'HAR004',  x: 280, y: 120, px: 160, py: 70,        label: 'HAR004',   type: 'leaf', cluster: 'CLU-001', cls: 'MDR-TB',     index: false },
    { id: 'HAR005',  x: 280, y: 150, px: 160, py: 70,        label: 'HAR005',   type: 'leaf', cluster: 'CLU-001', cls: 'Pre-XDR-TB', index: false },
    // CLU-003 leaves
    { id: 'MAN001',  x: 280, y: 165, px: 160, py: 180,       label: 'MAN001 ★', type: 'leaf', cluster: 'CLU-003', cls: 'DS-TB', index: true  },
    { id: 'MAN002',  x: 280, y: 195, px: 160, py: 180,       label: 'MAN002',   type: 'leaf', cluster: 'CLU-003', cls: 'DS-TB', index: false },
    // CLU-002 leaves
    { id: 'BUL001',  x: 280, y: 260, px: 160, py: 300,       label: 'BUL001 ★', type: 'leaf', cluster: 'CLU-002', cls: 'MDR-TB', index: true  },
    { id: 'BUL002',  x: 280, y: 295, px: 160, py: 300,       label: 'BUL002',   type: 'leaf', cluster: 'CLU-002', cls: 'MDR-TB', index: false },
    { id: 'BUL003',  x: 280, y: 330, px: 160, py: 300,       label: 'BUL003',   type: 'leaf', cluster: 'CLU-002', cls: 'MDR-TB', index: false },
  ];

  const getColor = (node) => {
    if (node.cluster === selectedCluster?.id) return T.cyan;
    if (node.cls) return CLS_COLOR[node.cls] || T.textMuted;
    if (node.lineage === 'L4') return '#27AE60';
    if (node.lineage === 'L2') return '#E74C3C';
    return T.textDim;
  };

  return (
    <svg viewBox="0 0 400 380" style={{ width: '100%', height: '100%' }}>
      <rect width="400" height="380" fill={T.bgElevated} rx="8" />

      {/* Branch lines */}
      {treeData.filter(n => n.px !== null).map(n => (
        <g key={`line-${n.id}`}>
          <line x1={n.px} y1={n.py} x2={n.px} y2={n.y} stroke={T.border} strokeWidth={1.5} />
          <line x1={n.px} y1={n.y}  x2={n.x}  y2={n.y} stroke={getColor(n)} strokeWidth={n.cluster === selectedCluster?.id ? 2.5 : 1.5}
            style={{ filter: n.cluster === selectedCluster?.id ? `drop-shadow(0 0 4px ${T.cyan})` : 'none' }} />
        </g>
      ))}

      {/* Nodes */}
      {treeData.map(n => {
        const col = getColor(n);
        const isHighlighted = n.cluster === selectedCluster?.id;
        return (
          <g key={n.id}>
            {n.type === 'leaf' ? (
              <>
                <circle cx={n.x} cy={n.y} r={n.index ? 6 : 4} fill={col} stroke={isHighlighted ? T.cyan : 'none'} strokeWidth={1.5}
                  style={{ filter: n.index ? `drop-shadow(0 0 4px ${col})` : 'none' }} />
                <text x={n.x + 10} y={n.y + 4} fontSize={9} fill={isHighlighted ? T.cyan : T.textMuted} fontFamily={MONO}>{n.label}</text>
                {n.cls && <text x={n.x + 10} y={n.y + 14} fontSize={7} fill={col} fontFamily={SANS}>{n.cls}</text>}
              </>
            ) : (
              <>
                <circle cx={n.x} cy={n.y} r={5} fill={T.bgElevated} stroke={col} strokeWidth={1.5} />
                <text x={n.x - 8} y={n.y + 4} textAnchor="end" fontSize={9} fill={col} fontFamily={SANS} fontWeight="600">{n.label}</text>
              </>
            )}
          </g>
        );
      })}

      {/* SNP scale bar */}
      <g transform="translate(20, 360)">
        <line x1={0} y1={0} x2={80} y2={0} stroke={T.textDim} strokeWidth={1} />
        <text x={40} y={-5} textAnchor="middle" fontSize={8} fill={T.textDim} fontFamily={SANS}>10 SNPs</text>
      </g>

      {/* Legend */}
      <g transform="translate(300, 20)">
        {[['★ Index case', T.cyan], ['Pre-XDR-TB', T.purple], ['MDR-TB', T.red], ['DS-TB', T.green]].map(([l, c], i) => (
          <g key={l} transform={`translate(0, ${i * 18})`}>
            <circle cx={5} cy={0} r={4} fill={c} />
            <text x={13} y={4} fontSize={8} fill={T.textMuted} fontFamily={SANS}>{l}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}

// ── SNP MATRIX TABLE ──────────────────────────────────────────────────
function SNPMatrixView() {
  const { samples, distances } = SNP_MATRIX;
  const getColor = (val) => {
    if (val === 0)   return { bg: T.bgElevated, text: T.textDim };
    if (val <= 12)   return { bg: T.red + '40',    text: T.red    };
    if (val <= 20)   return { bg: T.amber + '30',  text: T.amber  };
    if (val <= 50)   return { bg: T.blue + '20',   text: T.blueLight };
    return           { bg: T.bgPanel, text: T.textDim };
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10 }}>
        SNP distance matrix. <span style={{ color: T.red }}>Red ≤12 SNPs = probable transmission link</span> · <span style={{ color: T.amber }}>Amber ≤20 SNPs = possible link</span> · Grey = unrelated
      </div>
      <table style={{ borderCollapse: 'collapse', fontFamily: MONO, fontSize: 11 }}>
        <thead>
          <tr>
            <th style={{ padding: '6px 10px', color: T.textDim, fontSize: 10, textAlign: 'left' }}>Sample</th>
            {samples.map(s => (
              <th key={s} style={{ padding: '6px 8px', color: T.textMuted, fontSize: 9, textAlign: 'center', writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 70 }}>{s}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {distances.map((row, i) => (
            <tr key={samples[i]}>
              <td style={{ padding: '6px 10px', color: T.cyan, fontWeight: 600, whiteSpace: 'nowrap', borderRight: `1px solid ${T.border}` }}>{samples[i]}</td>
              {row.map((val, j) => {
                const cfg = getColor(val);
                return (
                  <td key={j} style={{ padding: '6px 10px', textAlign: 'center', background: cfg.bg, color: cfg.text, fontWeight: val <= 12 && val > 0 ? 700 : 400, border: `1px solid ${T.border}10` }}>
                    {val}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────
export default function App() {
  const [user,              setUser]              = useState(null);
  const [loginForm,         setLoginForm]         = useState({ username: '', password: '' });
  const [loginErr,          setLoginErr]          = useState('');
  const [activeTab,         setActiveTab]         = useState('map');
  const [selectedProvince,  setSelectedProvince]  = useState(null);
  const [selectedCluster,   setSelectedCluster]   = useState(null);
  const [mapOverlay,        setMapOverlay]         = useState('alert');
  const [alerts,            setAlerts]            = useState([...CLUSTERS.filter(c => c.alert_level === 'critical' || c.alert_level === 'high')]);

  const USERS = {
    'epi.analyst':   { id: 'u1', name: 'Epidemiology Analyst',   role: 'analyst',  password: 'Epi@2026'     },
    'moh.officer':   { id: 'u2', name: 'MoHCC TB Programme Officer', role: 'officer', password: 'MoH@2026'   },
    'lab.manager':   { id: 'u3', name: 'Laboratory Manager',     role: 'manager',  password: 'Manager@2026' },
  };

  const doLogin = () => {
    const u = USERS[loginForm.username.trim().toLowerCase()];
    if (!u || u.password !== loginForm.password) { setLoginErr('Invalid credentials'); return; }
    setUser(u); setLoginErr('');
  };

  const totalCases   = PROVINCES.reduce((s, p) => s + p.tb_cases,   0);
  const totalMDR     = PROVINCES.reduce((s, p) => s + p.mdr_cases,  0);
  const activeClusters = CLUSTERS.filter(c => c.status === 'Active').length;

  const TABS = [
    { id: 'map',       label: '🗺 Geographic Map'         },
    { id: 'clusters',  label: '🔺 Transmission Clusters'  },
    { id: 'phylo',     label: '🌳 Phylogenetic Analysis'  },
    { id: 'hotspots',  label: '🔥 Hotspot Analysis'       },
    { id: 'trends',    label: '📈 Temporal Trends'        },
    { id: 'sociodem',  label: '👥 Sociodemographics'      },
    { id: 'exports',   label: '📤 Export & Interop'       },
  ];

  // ── LOGIN ──
  if (!user) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg, position: 'relative', overflow: 'hidden' }}>
      <style>{css}</style>
      {/* Animated radar sweep */}
      <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', border: `1px solid ${T.border}`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', opacity: 0.3 }} />
      <div style={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', border: `1px solid ${T.border}`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', opacity: 0.2 }} />
      <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', border: `1px solid ${T.cyan}40`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />

      <div style={{ width: 420, position: 'relative', zIndex: 1, animation: 'fadeUp 0.4s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontFamily: MONO, color: T.textDim, letterSpacing: 3, marginBottom: 8 }}>TBGENSURV · MODULE 5</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: T.cyan, letterSpacing: -0.5, textShadow: `0 0 30px ${T.cyan}60` }}>Geospatial Surveillance</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 6 }}>Zimbabwe TB Transmission Mapping · MoHCC · BIBE UZ</div>
        </div>

        <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 28, boxShadow: `0 0 40px ${T.cyan}10` }}>
          {loginErr && <div style={{ marginBottom: 14, padding: '8px 12px', background: T.redDim, border: `1px solid ${T.red}40`, borderRadius: 6, fontSize: 12, color: T.red, fontFamily: MONO }}>⚠ {loginErr}</div>}
          {['username', 'password'].map(f => (
            <div key={f} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: T.textDim, fontFamily: MONO, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 1.2 }}>{f}</div>
              <input type={f === 'password' ? 'password' : 'text'} value={loginForm[f]} onChange={e => setLoginForm(p => ({ ...p, [f]: e.target.value }))}
                placeholder={f === 'username' ? 'epi.analyst' : '••••••••'}
                onKeyDown={e => e.key === 'Enter' && doLogin()}
                style={{ width: '100%', padding: '10px 14px', background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontFamily: MONO, fontSize: 13, outline: 'none' }} />
            </div>
          ))}
          <button onClick={doLogin} style={{ width: '100%', padding: 12, background: `linear-gradient(135deg, ${T.blue}, ${T.cyan}40)`, border: `1px solid ${T.cyan}50`, borderRadius: 8, color: T.white, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: MONO, letterSpacing: 0.5 }}>
            ACCESS SYSTEM →
          </button>
          <details style={{ marginTop: 14 }}>
            <summary style={{ fontSize: 11, color: T.textDim, cursor: 'pointer', fontFamily: MONO }}>credentials.txt ▾</summary>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(USERS).map(([un, u]) => (
                <div key={un} onClick={() => setLoginForm({ username: un, password: u.password })}
                  style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: T.bgElevated, borderRadius: 5, cursor: 'pointer', fontFamily: MONO, fontSize: 11, border: `1px solid ${T.border}` }}>
                  <span style={{ color: T.cyan }}>{un}</span>
                  <span style={{ color: T.textDim }}>{u.role}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <style>{css}</style>

      {/* Top bar */}
      <div style={{ background: T.bgCard, borderBottom: `1px solid ${T.border}`, padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: T.cyan }}>🌍 TBGenSurv · Geospatial</span>
          <span style={{ fontSize: 11, color: T.textDim, background: T.bgElevated, padding: '2px 10px', borderRadius: 99, border: `1px solid ${T.border}`, fontFamily: MONO }}>Module 5 · Zimbabwe · 2026</span>
          {alerts.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: T.redDim, borderRadius: 99, border: `1px solid ${T.red}40`, cursor: 'pointer' }} onClick={() => setActiveTab('clusters')}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.red, animation: 'pulse 1s infinite', display: 'inline-block' }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.red }}>{alerts.length} ACTIVE ALERT{alerts.length > 1 ? 'S' : ''}</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: T.textMuted }}>{user.name}</span>
          <button onClick={() => setUser(null)} style={{ padding: '5px 12px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, color: T.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: MONO }}>logout</button>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ background: T.bgElevated, borderBottom: `1px solid ${T.border}`, padding: '10px 24px', display: 'flex', gap: 32, overflowX: 'auto' }}>
        {[
          { label: 'Total TB Cases', value: totalCases.toLocaleString(), color: T.cyan,   icon: '🧬' },
          { label: 'MDR-TB Cases',   value: totalMDR.toLocaleString(),   color: T.red,    icon: '⚠' },
          { label: 'MDR Rate',       value: `${(totalMDR/totalCases*100).toFixed(1)}%`,   color: T.amber, icon: '📊' },
          { label: 'Active Clusters',value: activeClusters,              color: T.red,    icon: '🔺', pulse: true },
          { label: 'Provinces Hotspot', value: PROVINCES.filter(p=>p.hotspot).length,     color: T.amber, icon: '🔥' },
          { label: 'WGS Sequenced', value: '47 isolates',                color: T.green,  icon: '🔬' },
          { label: 'Transmission Links', value: CLUSTERS.reduce((s,c)=>s+c.transmission_links.length, 0), color: T.purple, icon: '🔗' },
        ].map(s => (
          <div key={s.label} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{s.icon}</span>
            <div>
              <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: s.color, animation: s.pulse ? 'pulse 2s infinite' : 'none' }}>{s.value}</div>
              <div style={{ fontSize: 10, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ background: T.bgCard, borderBottom: `1px solid ${T.border}`, padding: '0 24px', display: 'flex', gap: 0, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '10px 16px', border: 'none', borderBottom: `2px solid ${activeTab === t.id ? T.cyan : 'transparent'}`, background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: activeTab === t.id ? T.cyan : T.textMuted, whiteSpace: 'nowrap', transition: 'color 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: 24, maxWidth: 1600, margin: '0 auto', width: '100%' }}>

        {/* ── MAP ── */}
        {activeTab === 'map' && (
          <div className="fade-up">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text }}>Zimbabwe TB Geographic Distribution</h2>
                <p style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Province-level TB burden · Cluster locations · {new Date().toLocaleDateString()} snapshot</p>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['alert', 'Alert Level'], ['rate', 'TB Rate/100k'], ['mdr', 'MDR% Rate']].map(([v, l]) => (
                  <button key={v} onClick={() => setMapOverlay(v)} style={{ padding: '6px 12px', borderRadius: 99, border: `1px solid ${mapOverlay === v ? T.cyan : T.border}`, background: mapOverlay === v ? T.cyanDim : T.bgCard, color: mapOverlay === v ? T.cyan : T.textMuted, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{l}</button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>
              <div style={{ height: 480, borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                <ZimbabweMap provinces={PROVINCES} clusters={CLUSTERS} selectedProvince={selectedProvince} onSelectProvince={p => setSelectedProvince(p === selectedProvince ? null : p)} overlay={mapOverlay} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Province detail */}
                {selectedProvince ? (
                  <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '16px', flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{selectedProvince.name}</div>
                      {badge(ALERT_CFG[selectedProvince.alert].label, ALERT_CFG[selectedProvince.alert].color, selectedProvince.alert === 'critical')}
                    </div>
                    {[
                      ['TB Cases',     selectedProvince.tb_cases.toLocaleString(), T.cyan],
                      ['MDR-TB Cases', selectedProvince.mdr_cases,                T.red],
                      ['MDR Rate',     `${(selectedProvince.mdr_cases/selectedProvince.tb_cases*100).toFixed(1)}%`, T.amber],
                      ['TB Rate/100k', selectedProvince.tb_rate,                  T.blueLight],
                      ['Population',   selectedProvince.population.toLocaleString(), T.textMuted],
                    ].map(([l, v, c]) => (
                      <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ fontSize: 12, color: T.textMuted }}>{l}</span>
                        <span style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: c }}>{v}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, color: T.textDim, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Districts</div>
                      {selectedProvince.districts.map(d => (
                        <div key={d.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 8px', background: T.bgElevated, borderRadius: 5, marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: T.textMuted }}>{d.name}</span>
                          <span style={{ fontFamily: MONO, fontSize: 11, color: d.rate > 200 ? T.red : T.text }}>{d.cases} ({d.rate}/100k)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, flex: 1 }}>
                    <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>Click a province circle to view details</div>
                    <div style={{ fontSize: 11, color: T.textDim, marginBottom: 8 }}>PROVINCE RANKING BY TB RATE</div>
                    {[...PROVINCES].sort((a,b) => b.tb_rate - a.tb_rate).map((p, i) => (
                      <div key={p.id} onClick={() => setSelectedProvince(p)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}>
                        <span style={{ fontSize: 11, color: T.textDim, width: 16, fontFamily: MONO }}>{i+1}</span>
                        <span style={{ fontSize: 12, color: T.text, flex: 1 }}>{p.name}</span>
                        <div style={{ width: 60, height: 5, background: T.bgElevated, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(p.tb_rate / 450) * 100}%`, background: p.hotspot ? T.red : T.blueLight, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontFamily: MONO, fontSize: 11, color: p.hotspot ? T.red : T.textMuted, width: 40, textAlign: 'right' }}>{p.tb_rate}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Map legend */}
                <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 10, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>Legend</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {[['● Critical hotspot', T.red], ['● High burden', T.amber], ['● Moderate', T.blueLight], ['● Low burden', T.green], ['▲ Transmission cluster', T.red]].map(([l, c]) => (
                      <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: c }}>{l}</div>
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: T.textDim, marginTop: 8 }}>Circle size = TB case volume</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── CLUSTERS ── */}
        {activeTab === 'clusters' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>WGS-Confirmed Transmission Clusters</h2>
            <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Clusters identified by SNP distance ≤12 from whole genome sequencing · IQ-TREE2 maximum likelihood phylogenetics</p>
            <div style={{ display: 'grid', gridTemplateColumns: selectedCluster ? '1fr 1fr' : '1fr', gap: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {CLUSTERS.map(c => {
                  const alert = ALERT_CFG[c.alert_level];
                  const isSelected = selectedCluster?.id === c.id;
                  return (
                    <div key={c.id} onClick={() => setSelectedCluster(isSelected ? null : c)}
                      style={{ background: T.bgCard, border: `2px solid ${isSelected ? T.cyan : T.border}`, borderLeft: `4px solid ${alert.color}`, borderRadius: 10, padding: '16px 20px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: isSelected ? `0 0 20px ${T.cyan}15` : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>{c.name}</div>
                          <div style={{ fontSize: 12, color: T.textMuted }}>{c.location.district} · {c.location.province}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
                          {badge(alert.label, alert.color, c.status === 'Active' && c.alert_level === 'critical')}
                          <span style={{ fontSize: 10, color: c.status === 'Active' ? T.green : T.textDim, fontWeight: 600 }}>{c.status}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                        {[
                          ['Cluster size', `${c.size} cases`,  T.cyan],
                          ['Classification', c.classification, CLS_COLOR[c.classification] || T.textMuted],
                          ['Lineage', c.lineage, T.amber],
                          ['Avg SNP dist', `${c.snp_distance}`, T.blueLight],
                        ].map(([l, v, col]) => (
                          <div key={l}>
                            <div style={{ fontSize: 9, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{l}</div>
                            <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: col }}>{v}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {c.key_mutations.map(m => (
                          <span key={m} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: T.redDim, color: T.red, border: `1px solid ${T.red}30`, fontFamily: MONO }}>{m}</span>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>{c.notes}</div>
                    </div>
                  );
                })}
              </div>

              {selectedCluster && (
                <div style={{ background: T.bgCard, border: `1px solid ${T.cyan}40`, borderRadius: 10, padding: '16px 20px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.cyan, marginBottom: 12 }}>🔗 Transmission Network — {selectedCluster.name}</div>
                  <div style={{ height: 280, marginBottom: 16 }}>
                    <svg viewBox="0 0 300 240" style={{ width: '100%', height: '100%' }}>
                      <rect width="300" height="240" fill={T.bgElevated} rx="8" />
                      {/* Transmission links */}
                      {selectedCluster.transmission_links.map((link, i) => {
                        const from = selectedCluster.isolates.find(iso => iso.id === link.from);
                        const to   = selectedCluster.isolates.find(iso => iso.id === link.to);
                        if (!from || !to) return null;
                        const fromIdx = selectedCluster.isolates.indexOf(from);
                        const toIdx   = selectedCluster.isolates.indexOf(to);
                        const fx = 80, fy = 40 + fromIdx * 45;
                        const tx = 210, ty = 40 + toIdx * 45;
                        return (
                          <g key={i}>
                            <line x1={fx} y1={fy} x2={tx} y2={ty} stroke={link.confidence === 'High' ? T.cyan : T.amber} strokeWidth={link.confidence === 'High' ? 2 : 1} strokeDasharray={link.confidence === 'High' ? 'none' : '4,3'} />
                            <text x={(fx+tx)/2} y={(fy+ty)/2 - 5} fontSize={8} fill={T.textDim} textAnchor="middle" fontFamily={MONO}>{link.snp_dist} SNPs</text>
                          </g>
                        );
                      })}
                      {/* Isolate nodes */}
                      {selectedCluster.isolates.map((iso, i) => {
                        const col = CLS_COLOR[iso.classification] || T.textMuted;
                        const x = iso.index_case ? 80 : 210;
                        const y = 40 + i * 45;
                        return (
                          <g key={iso.id}>
                            {iso.index_case && <circle cx={x} cy={y} r={24} fill="none" stroke={col} strokeWidth={1} strokeDasharray="3,3" opacity={0.4} />}
                            <circle cx={x} cy={y} r={iso.index_case ? 16 : 12} fill={col + '30'} stroke={col} strokeWidth={iso.index_case ? 2.5 : 1.5}
                              style={{ filter: `drop-shadow(0 0 4px ${col})` }} />
                            <text x={x} y={y + 4} textAnchor="middle" fontSize={8} fill={T.white} fontFamily={MONO} fontWeight="700">{iso.index_case ? '★' : iso.id.slice(-3)}</text>
                            <text x={iso.index_case ? x - 30 : x + 30} y={y + 4} textAnchor={iso.index_case ? 'end' : 'start'} fontSize={8} fill={T.textMuted} fontFamily={MONO}>{iso.sample_code.slice(-4)}</text>
                            <text x={iso.index_case ? x - 30 : x + 30} y={y + 14} textAnchor={iso.index_case ? 'end' : 'start'} fontSize={7} fill={col} fontFamily={SANS}>{iso.classification}</text>
                          </g>
                        );
                      })}
                      <text x={80} y={220} textAnchor="middle" fontSize={8} fill={T.textDim} fontFamily={SANS}>Index case</text>
                      <text x={210} y={220} textAnchor="middle" fontSize={8} fill={T.textDim} fontFamily={SANS}>Secondary cases</text>
                    </svg>
                  </div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10 }}>
                    First detected: {selectedCluster.first_detected} · Last detected: {selectedCluster.last_detected}
                  </div>
                  <div style={{ display: 'flex', gap: 10, fontSize: 10, color: T.textDim }}>
                    <span style={{ color: T.cyan }}>━━ High confidence (High SNP similarity)</span>
                    <span style={{ color: T.amber }}>╌╌ Moderate confidence</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── PHYLOGENETICS ── */}
        {activeTab === 'phylo' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>Phylogenetic Analysis</h2>
            <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Maximum likelihood tree (IQ-TREE2) · GTR+G substitution model · H37Rv reference root · {PHYLO_TREE.totalLeaves} isolates</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>Phylogenetic Tree — Click a cluster in the Clusters tab to highlight</div>
                <div style={{ height: 400, borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                  <PhyloTree selectedCluster={selectedCluster} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>SNP Distance Matrix — Pairwise distances between sequenced isolates</div>
                <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, height: 400, overflowY: 'auto' }}>
                  <SNPMatrixView />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── HOTSPOTS ── */}
        {activeTab === 'hotspots' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>Spatial Hotspot Analysis — Getis-Ord Gi*</h2>
            <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Statistically significant TB burden clusters identified using the Getis-Ord Gi* spatial autocorrelation statistic. Positive Gi* = hot spot, Negative Gi* = cold spot.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Gi* Statistics by Province</div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={HOTSPOTS} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <XAxis type="number" tick={{ fontSize: 10, fill: T.textMuted }} />
                    <YAxis type="category" dataKey="province" tick={{ fontSize: 11, fill: T.textMuted }} width={90} />
                    <Tooltip contentStyle={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12 }} />
                    <ReferenceLine x={0} stroke={T.border} />
                    <ReferenceLine x={1.96} stroke={T.amber} strokeDasharray="4,3" label={{ value: '95% CI', fill: T.amber, fontSize: 9 }} />
                    <ReferenceLine x={2.58} stroke={T.red} strokeDasharray="4,3" label={{ value: '99% CI', fill: T.red, fontSize: 9 }} />
                    <Bar dataKey="gi_star" name="Gi* Statistic" radius={[0,4,4,0]}>
                      {HOTSPOTS.map((h, i) => <Cell key={i} fill={h.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Hotspot Classification</div>
                {HOTSPOTS.map(h => (
                  <div key={h.province} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{h.province}</span>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontFamily: MONO, fontSize: 12, color: T.textMuted }}>Gi*: {h.gi_star.toFixed(2)}</span>
                      <span style={{ fontFamily: MONO, fontSize: 11, color: T.textDim }}>p={h.pvalue}</span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, color: h.color, background: h.color + '20', border: `1px solid ${h.color}30`, fontWeight: 600 }}>{h.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── TRENDS ── */}
        {activeTab === 'trends' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>Temporal Trends — Zimbabwe TB Notifications</h2>
            <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Monthly TB case notifications Jan 2025 – Mar 2026. MDR and XDR-TB trends show upward trajectory.</p>
            <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: 24, marginBottom: 20 }}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={TREND_DATA} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: T.textMuted }} />
                  <YAxis tick={{ fontSize: 10, fill: T.textMuted }} />
                  <Tooltip contentStyle={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="total" stroke={T.cyan}   strokeWidth={2.5} dot={false} name="Total TB" />
                  <Line type="monotone" dataKey="mdr"   stroke={T.red}    strokeWidth={2}   dot={false} name="MDR-TB" />
                  <Line type="monotone" dataKey="xdr"   stroke={T.purple} strokeWidth={2}   dot={false} name="Pre-XDR/XDR-TB" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ padding: '14px 18px', background: T.redDim, border: `1px solid ${T.red}40`, borderRadius: 10, fontSize: 13, color: T.red }}>
              ⚠ <strong>Trend Alert:</strong> MDR-TB notifications increased 51% over the 15-month period (Jan 2025–Mar 2026). Pre-XDR-TB cases increased 175%. TBGenSurv transmission cluster analysis has identified two active WGS-confirmed clusters (Harare Pre-XDR, Bulawayo MDR) contributing to this trend. MoHCC notification sent.
            </div>
          </div>
        )}

        {/* ── SOCIODEM ── */}
        {activeTab === 'sociodem' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>Sociodemographic Overlay</h2>
            <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Correlation between TB burden and key sociodemographic determinants across Zimbabwe provinces</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {[
                { key: 'hiv_prev', label: 'HIV Prevalence (%)', color: T.red    },
                { key: 'poverty_pct', label: 'Poverty Rate (%)', color: T.amber },
                { key: 'healthcare_access', label: 'Healthcare Access (%)', color: T.green },
                { key: 'population_density', label: 'Population Density (per km²)', color: T.cyan },
              ].map(({ key, label, color }) => (
                <div key={key} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>{label} vs TB Rate</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={PROVINCES.map(p => ({ name: p.name.slice(0,4), tbRate: p.tb_rate, value: SOCIODEM[p.id]?.[key] || 0 }))} margin={{ top: 0, right: 10, bottom: 0, left: -10 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: T.textMuted }} />
                      <YAxis tick={{ fontSize: 9, fill: T.textMuted }} />
                      <Tooltip contentStyle={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 11 }} />
                      <Bar dataKey="value" fill={color} radius={[3,3,0,0]} name={label} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── EXPORTS ── */}
        {activeTab === 'exports' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>Export & Interoperability</h2>
            <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Export surveillance data in standardised formats for national and global health information systems</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {[
                { name: 'WHO GLASS Export',   desc: 'Global Antimicrobial Resistance & Use Surveillance System. Exports drug resistance profiles and lineage data.',             icon: '🌍', color: T.cyan,   format: 'JSON · GLASS v2024',     status: 'Ready'   },
                { name: 'Africa CDC PGI',      desc: 'Africa Pathogen Genomics Initiative continental surveillance. Exports WGS metadata, phylogenetic clusters.',             icon: '🧬', color: T.green,  format: 'JSON · FHIR R4',         status: 'Ready'   },
                { name: 'DHIS2 Data Values',   desc: 'Zimbabwe MoHCC national DHIS2 instance. Province and district-level TB case aggregates.',                                icon: '🇿🇼', color: T.amber,  format: 'JSON · DHIS2 API v2',   status: 'Ready'   },
                { name: 'GeoJSON Map Layers',  desc: 'Province-level choropleth and cluster point data for GIS applications (QGIS, ArcGIS, Leaflet).',                       icon: '🗺', color: T.blue,   format: 'GeoJSON · WGS84',        status: 'Ready'   },
                { name: 'Phylogenetic Export', desc: 'Newick tree format and SNP distance matrix for phylogenetic analysis tools (IQ-TREE2, BEAST2, FigTree).',              icon: '🌳', color: T.purple, format: 'Newick + CSV matrix',    status: 'Ready'   },
                { name: 'MoHCC Situation Report', desc: 'Formatted TB surveillance situation report for Ministry of Health and Child Care monthly reporting.',                icon: '📋', color: T.teal,   format: 'PDF · MoHCC template',   status: 'Beta'    },
              ].map(exp => (
                <div key={exp.name} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>{exp.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: exp.color, marginBottom: 6 }}>{exp.name}</div>
                  <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 12, lineHeight: 1.6 }}>{exp.desc}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, fontFamily: 'monospace', color: T.textDim, background: T.bgElevated, padding: '2px 7px', borderRadius: 4 }}>{exp.format}</span>
                    <button onClick={() => {
                      const data = { system: exp.name, exported_at: new Date().toISOString(), source: 'TBGenSurv Module 5 · University of Zimbabwe BIBE', data: { provinces: PROVINCES.length, clusters: CLUSTERS.length, total_cases: totalCases, mdr_cases: totalMDR } };
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = `TBGenSurv_${exp.name.replace(/\s+/g,'_')}.json`; a.click();
                    }} style={{ padding: '6px 14px', background: exp.color + '20', border: `1px solid ${exp.color}40`, borderRadius: 6, color: exp.color, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      ⬇ Export
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 24px', background: T.bgCard, borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.textDim, fontFamily: MONO }}>
        <span>TBGenSurv Module 5 · Geospatial & Phylogenetic Surveillance · University of Zimbabwe BIBE · MoHCC Zimbabwe</span>
        <span>Getis-Ord Gi* · IQ-TREE2 · WHO GLASS · Africa CDC PGI · DHIS2 · GeoJSON</span>
      </div>
    </div>
  );
}
